"use client";

import { useRef } from "react";
import { css } from "styled-system/css";
import { gsap, useGSAP } from "@/lib/gsap-setup";

type IntegrationItem = {
  label: string;
  brand: "mercado-pago" | "google-calendar" | "whatsapp" | "email" | "google-meet" | "gmail";
};

const INTEGRATIONS: IntegrationItem[] = [
  { label: "Mercado Pago", brand: "mercado-pago" },
  { label: "Google Calendar", brand: "google-calendar" },
  { label: "WhatsApp", brand: "whatsapp" },
  { label: "Gmail", brand: "gmail" },
  { label: "Google Meet", brand: "google-meet" },
  { label: "Email", brand: "email" },
];

// Two copies: at ~210px/chip × 6 unique × 2 = ~2520px — wider than any desktop viewport,
// so the same integration is never visible twice simultaneously.
const TRACK_ITEMS = [...INTEGRATIONS, ...INTEGRATIONS];

function BrandLogo({ brand }: { brand: IntegrationItem["brand"] }) {
  const imgClass = css({ w: "5", h: "5", flexShrink: 0, objectFit: "contain" });

  if (brand === "mercado-pago") {
    return <img src="/brands/mercado-pago.png" alt="" aria-hidden className={imgClass} />;
  }
  if (brand === "google-calendar") {
    return (
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg"
        alt=""
        aria-hidden
        className={css({ w: "5", h: "5", flexShrink: 0, rounded: "md" })}
      />
    );
  }
  if (brand === "whatsapp") {
    return (
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
        alt=""
        aria-hidden
        className={css({ w: "5", h: "5", rounded: "full", flexShrink: 0 })}
      />
    );
  }
  if (brand === "google-meet") {
    return (
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/9/9b/Google_Meet_icon_%282020%29.svg"
        alt=""
        aria-hidden
        className={css({ w: "5", h: "5", flexShrink: 0 })}
      />
    );
  }
  if (brand === "gmail") {
    return (
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg"
        alt=""
        aria-hidden
        className={css({ w: "5", h: "5", flexShrink: 0 })}
      />
    );
  }
  // email fallback
  return (
    <img
      src="https://img.icons8.com/fluency/48/mail.png"
      alt=""
      aria-hidden
      className={css({ w: "5", h: "5", rounded: "lg", flexShrink: 0 })}
    />
  );
}

function IntegrationChip({ label, brand }: IntegrationItem) {
  return (
    <div
      className={css({
        display: "inline-flex",
        alignItems: "center",
        gap: "2.5",
        flexShrink: 0,
        px: "5",
        py: "3",
        rounded: "full",
        fontSize: "sm",
        fontWeight: "semibold",
        color: "rgba(255,255,255,0.92)",
        bg: "rgba(255,255,255,0.08)",
        borderWidth: "1px",
        borderColor: "rgba(255,255,255,0.14)",
      })}
    >
      <BrandLogo brand={brand} />
      <span>{label}</span>
    </div>
  );
}

export function IntegrationsMarquee() {
  const trackRef = useRef<HTMLDivElement>(null);
  const firstSetRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const track = trackRef.current;
      const first = firstSetRef.current;
      if (!track || !first) return;

      const run = () => {
        const w = first.offsetWidth;
        if (w <= 0) return;
        gsap.killTweensOf(track);
        gsap.set(track, { x: 0 });
        gsap.to(track, {
          x: -w,
          duration: Math.max(28, w / 40),
          ease: "none",
          repeat: -1,
        });
      };

      run();

      const ro = new ResizeObserver(() => {
        run();
      });
      ro.observe(first);

      return () => {
        ro.disconnect();
        gsap.killTweensOf(track);
      };
    },
    { scope: trackRef },
  );

  const chipRow = (ref?: React.RefObject<HTMLDivElement | null>, ariaHidden?: boolean) => (
    <div
      ref={ref}
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "4",
        flexShrink: 0,
        pr: "4",
      })}
      aria-hidden={ariaHidden}
    >
      {TRACK_ITEMS.map(({ label, brand }, idx) => (
        <IntegrationChip key={`${label}-${idx}`} label={label} brand={brand} />
      ))}
    </div>
  );

  return (
    <div
      className={css({
        position: "relative",
        w: "100%",
        overflow: "hidden",
        maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
        WebkitMaskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
      })}
    >
      <div
        ref={trackRef}
        className={css({
          display: "flex",
          w: "max-content",
          willChange: "transform",
        })}
      >
        {chipRow(firstSetRef)}
        {chipRow(undefined, true)}
      </div>
    </div>
  );
}
